import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Form, Col } from "react-bootstrap";
import { estados, statusExame, statusAplicacoes, getCurrentUser } from '../../common/utils';
import AsyncSelect from 'react-select/async';
import api from "../../services/api";

/* helpers de data */
const pad = (n) => String(n).padStart(2, "0");
const fmtYMD = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseYMD = (s) => {
    if (!s) return null;
    const [y, m, d] = s.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    return isNaN(dt.getTime()) ? null : dt;
};
const today = () => {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), n.getDate());
};

const carregaDoencas = async (inputValue, programa_id) => {
    if(!programa_id) {
        return [];
    }

    try {
        const response = await api.get(`/programas/doencas/?programa_id=${programa_id}&nome=${inputValue}`);
        const opcoes = response.data.results.map(doenca => ({
            value: doenca.dados_doenca.id,
            label: doenca.dados_doenca.nome
        }));
        return opcoes;
    } catch (error) {
        console.error('Erro ao carregar doenças:', error);
        return [];
    }
};

const carregarMedicos = async (busca, programa_id) => {
    const dados_usuario = getCurrentUser();
    try {
        const response = await api.get(`/pessoas/prestadores/?programa_id=${programa_id}&categoria=medicos&nome=${busca}&tipo_usuario=${dados_usuario.tipo_usuario}`);
        //const response = await api.get(`/programas/aplicacoes-status/`);
        const opcoesFiltradas = response.data.results.map(medico => ({
            value: medico.id,
            label: medico.nome,
            crm: medico.registro + ' - ' + medico.registro_uf,
        }));

        return opcoesFiltradas;
    } catch (error) {
        console.error('Erro ao carregar médicos:', error);
        Sentry.captureException('Erro ao carregar médicos:', error);
    }
};

const carregaAtendentes = async (inputValue, programa_id) => {
    try {
        const response = await api.get(`/pessoas/operadores/?nome=${inputValue}&programa_id=${programa_id}`);
        const opcoes = response.data.results.map(atendente => ({
            value: atendente.id,
            label: atendente.dados_pessoa.nome
        }));
        return opcoes;
    } catch (error) {
        console.error('Erro ao carregar atendentes:', error);
        //Sentry.captureException('Erro ao carregar atendentes:', error);
        return [];
    }
};

/* resolvers: cada um lê `values`, faz parse se precisar e retorna uma Date */
const RESOLVERS = {
    // usa o ano de data_ate (se vazio/ inválido, usa hoje)
    primeiroDiaAnoReferenteDataAte: (values) => {
        const ref = parseYMD(values?.data_ate) || today();
        return new Date(ref.getFullYear(), 0, 1);
    },

    // último dia do mês anterior a data_ate (ou de hoje, se vazio/ inválido)
    ultimoDiaMesAnterior: (values) => {
        const base = parseYMD(values?.data_ate) || today();
        return new Date(base.getFullYear(), base.getMonth(), 0);
    },

    // sete dias atras a partir da data atual
    seteDiasAtras: () => {
        const base = new Date();              // data atual
        base.setHours(0, 0, 0, 0);            // zera horas/minutos
        base.setDate(base.getDate() - 7);     // subtrai 7 dias
        return base;                          // retorna objeto Date
    },

    // data atual
    dataAtual: () => {
        const base = new Date();
        base.setHours(0, 0, 0, 0);
        return base;
    },

    primeiroDiaMesAtual: () => {
        const base = new Date();
        base.setHours(0, 0, 0, 0);
        return new Date(base.getFullYear(), base.getMonth(), 1);
    },

    ultimoDiaMesAtual: () => {
        const base = new Date();
        base.setHours(0, 0, 0, 0);
        // dia 0 do mês seguinte => último dia do mês atual
        return new Date(base.getFullYear(), base.getMonth() + 1, 0);
    },
};

export default function FiltrosDinamicos({ config, onChange, programa_id }) {
    const keys = useMemo(() => Object.keys(config || {}), [config]);
    const [values, setValues] = useState(() =>
        Object.fromEntries(keys.map((k) => [k, ""]))
    );

    // re-sincroniza chaves novas/removidas
    useEffect(() => {
        const base = Object.fromEntries(keys.map((k) => [k, ""]));
        setValues(base);
    }, [keys]);

    // calcula sugestões só para campos de tipo "date" e preenche apenas se estiver vazio
    useEffect(() => {
        const suggestions = {};
        for (const k of keys) {
            const def = config[k];
            if (def.input !== "date") continue;

            const fn = RESOLVERS[def.funcao];
            if (!fn) continue;

            const out = fn(values); // resolver faz a conversão internamente
            if (out instanceof Date && !isNaN(out)) suggestions[k] = fmtYMD(out);
        }

        setValues((prev) => {
            let changed = false;
            const next = { ...prev };
            for (const k of keys) {
                if (suggestions[k] && !next[k]) {
                    next[k] = suggestions[k];
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
    }, [config, keys, values.data_ate]); // depende de data_ate para recalcular o outro

    // propaga pro pai
    useEffect(() => {
        onChange && onChange(values);
    }, [values, onChange]);

    const label = (k) =>
        k.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());

    return (
        <>
            {keys.map((k) => {
                const def = config[k];
                const type = ["date", "text", "number"].includes(def.input) ? def.input : "text";

                // select de estados
                if(def.input === "select_ufs"){
                    return (
                        <Col key={k} sm={3} xs={12}>
                            <Form.Group className="mb-3">
                                <Form.Label>{label(k)}</Form.Label>
                                    <Form.Control as="select" 
                                        value={values[k] ?? ""}
                                        onChange={(e) => setValues((v) => ({ ...v, [k]: e.target.value }))}
                                    >
                                        <option value="">Selecione...</option>
                                        {Object.entries(estados).map(([sigla, nome]) => (
                                            <option key={sigla} value={sigla}>
                                                {nome}
                                            </option>
                                        ))}
                                    </Form.Control>
                            </Form.Group>
                        </Col>
                    );
                }
                // select de status de exames
                else if(def.input === "select_status_exames"){
                    return (
                        <Col key={k} sm={3} xs={12}>
                            <Form.Group className="mb-3">
                                <Form.Label>{label(k)}</Form.Label>
                                <Form.Control as="select" 
                                    value={values[k] ?? ""}
                                    onChange={(e) => setValues((v) => ({ ...v, [k]: e.target.value }))}
                                >
                                    <option value="">Todos os Status</option>
                                    {Object.entries(statusExame).map(([key, value]) => (
                                        <option key={key} value={value}>{value}</option>
                                    ))}
                                </Form.Control>
                            </Form.Group>
                        </Col>
                    );
                }
                // select de status de aplicacoes
                else if(def.input === "select_status_aplicacao"){
                    return (
                        <Col key={k} sm={3} xs={12}>
                            <Form.Group className="mb-3">
                                <Form.Label>{label(k)}</Form.Label>
                                <Form.Control as="select" 
                                    value={values[k] ?? ""}
                                    onChange={(e) => setValues((v) => ({ ...v, [k]: e.target.value }))}
                                >
                                    <option value="">Todos os Status</option>
                                    {Object.entries(statusAplicacoes).map(([key, value]) => (
                                        <option key={key} value={value}>{value}</option>
                                    ))}
                                </Form.Control>
                            </Form.Group>
                        </Col>
                    );
                }
                // select de patologia
                else if(def.input === "select_patologias"){
                    return (
                        <Col key={k} sm={3} xs={12}>
                            <Form.Group className="mb-3">
                                <Form.Label>{label(k)}</Form.Label>
                                <AsyncSelect                                  
                                    cacheOptions
                                    loadOptions={(inputValue) => carregaDoencas(inputValue, programa_id)}
                                    defaultOptions
                                    placeholder='Selecionar patologia...'
                                    noOptionsMessage={() => "Nenhuma patologia encontrada"}
                                    value={values[k] || null}
                                    onChange={(option) =>
                                        setValues((v) => ({ ...v, [k]: option })) // salva o objeto todo
                                    }
                                    isClearable
                                />
                            </Form.Group>
                        </Col>
                    );
                }
                // select de medicos
                else if(def.input === "select_medicos"){
                    return (
                        <Col key={k} sm={3} xs={12}>
                            <Form.Group className="mb-3">
                                <Form.Label>{label(k)}</Form.Label>
                                <AsyncSelect
                                    className="rs-cinza"
                                    classNamePrefix="rs"
                                    loadOptions={(inputValue) => carregarMedicos(inputValue, programa_id)}
                                    defaultOptions
                                    onChange={(option) =>
                                        setValues((v) => ({ ...v, [k]: option })) // salva o objeto todo
                                    }
                                    isClearable
                                    placeholder="Buscar médico..."
                                    noOptionsMessage={() => "Nenhum médico encontrado"}
                                    value={values[k] || null}
                                />
                            </Form.Group>
                        </Col>
                    );
                }                
                // select de navegadoras
                else if(def.input === "select_navegadoras"){
                    return (
                        <Col key={k} sm={3} xs={12}>
                            <Form.Group className="mb-3">
                                <Form.Label>{label(k)}</Form.Label>
                                <AsyncSelect
                                    cacheOptions
                                    loadOptions={(inputValue) => carregaAtendentes(inputValue, programa_id)}
                                    defaultOptions
                                    placeholder={'Buscar navegador...'}
                                    noOptionsMessage={() => "Nenhum navegador encontrado"}
                                    onChange={(option) =>
                                        setValues((v) => ({ ...v, [k]: option })) // salva o objeto todo
                                    }
                                    isClearable
                                    value={values[k] || null}
                                />
                            </Form.Group>
                        </Col>
                    );
                }
                // select genérico: as opções vêm do próprio filtro do modelo
                // ({"input": "select_opcoes", "opcoes": [{"valor": "...", "label": "..."}], "placeholder": "..."})
                else if(def.input === "select_opcoes"){
                    return (
                        <Col key={k} sm={3} xs={12}>
                            <Form.Group className="mb-3">
                                <Form.Label>{label(k)}</Form.Label>
                                <Form.Control as="select"
                                    value={values[k] ?? ""}
                                    onChange={(e) => setValues((v) => ({ ...v, [k]: e.target.value }))}
                                >
                                    <option value="">{def.placeholder || "Todos"}</option>
                                    {(def.opcoes || []).map((op) => (
                                        <option key={op.valor} value={op.valor}>{op.label}</option>
                                    ))}
                                </Form.Control>
                            </Form.Group>
                        </Col>
                    );
                }
                // select de categorias de prestadores
                else if(def.input === "select_categorias_prestadores"){
                    const prestadoresCategorias = {
                        "multiprofissionais": "Multiprofissionais",
                        "farmacias": "Farmácias",
                        "hospitais": "Hospitais",
                        "clinicas": "Clínicas",
                        "plataformas": "Plataformas",
                        "servicos_diagnostico": "Serviços Diagnóstico",
                        "laboratorio_diagnostico": "Laboratórios de Diagnósticos",
                        "cooperativas_saude": "Cooperativas de Saúde",
                        "motoristas": "Transporte",
                        "logistica": "Logística",
                        "outros": "Outros"
                    };

                    return (
                        <Col key={k} sm={3} xs={12}>
                            <Form.Group className="mb-3">
                                <Form.Label>{label(k)}</Form.Label>
                                <Form.Control as="select" 
                                    value={values[k] ?? ""}
                                    onChange={(e) => setValues((v) => ({ ...v, [k]: e.target.value }))}
                                >
                                    <option value="">Todos os Status</option>
                                    {Object.entries(prestadoresCategorias).map(([key, value]) => (
                                        <option key={key} value={key}>{value}</option>
                                    ))}
                                </Form.Control>
                            </Form.Group>
                        </Col>
                    );
                }                
                // inputs normais
                else {
                    return (
                        <Col key={k} sm={3} xs={12}>
                            <Form.Group className="mb-3">
                                <Form.Label>{label(k)}</Form.Label>
                                    <Form.Control
                                        type={type}
                                        value={values[k] || ""}
                                        onChange={(e) => setValues((v) => ({ ...v, [k]: e.target.value }))}
                                    />
                            </Form.Group>
                        </Col>
                    );
                }
            })}
        </>
    );
}
